import { doc, collection, getDoc, getDocs, query, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import type {
  GPSummaryData,
  GPSummaryUserInfo,
  GPSummarySnapshot,
  GPSummaryMeta,
  MoodWeek,
  SymptomSummary,
  RedFlag,
  BleedingDay,
  WeekActivities,
  WeekActivity,
  FlowLevel,
  BloodColour,
  ClotSize,
} from "../types/gpSummary";

const SYMPTOM_LABELS: Record<string, { label: string; levels: Record<string, string> }> = {
  fatigue: {
    label: "Fatigue",
    levels: { mild: "Well rested", moderate: "A bit tired", severe: "Exhausted" },
  },
  perinealPain: {
    label: "Perineal Pain",
    levels: { none: "None", mild: "Mild", moderate: "Moderate", severe: "Severe" },
  },
  cSectionWound: {
    label: "C-Section Wound",
    levels: { none: "None", mild: "Mild", moderate: "Moderate", severe: "Severe" },
  },
  breastfeedingPain: {
    label: "Breastfeeding Pain",
    levels: { none: "None", moderate: "Some discomfort", severe: "Severe pain" },
  },
  bladderControl: {
    label: "Bladder Control",
    levels: { none: "No leaks", mild: "Once", moderate: "Often", severe: "Always" },
  },
  bowelFunction: {
    label: "Bowel Function",
    levels: { mild: "No trouble", moderate: "A bit constipated", severe: "Very constipated" },
  },
  pelvicHeaviness: {
    label: "Pelvic Heaviness",
    levels: { none: "None", mild: "Mild", moderate: "Moderate", severe: "Severe" },
  },
  painAndMovement: {
    label: "Pain & Movement",
    levels: { none: "No limiting pain", mild: "Some limitation", severe: "A lot" },
  },
};

const SEVERITY_RANK: Record<string, number> = {
  none: 0, mild: 1, moderate: 2, severe: 3, sever: 3, some: 1, a_lot: 3,
};

const ALERT_LABELS: Record<string, string> = {
  vaginalBleeding: "Heavy bleeding alert",
  perinealPain: "Severe perineal pain alert",
  cSectionWound: "Wound concern alert",
  fatigue: "Severe fatigue alert",
  breastfeedingPain: "Breastfeeding pain alert",
  bladderControl: "Bladder control alert",
  bowelFunction: "Bowel function alert",
  pelvicHeaviness: "Pelvic heaviness alert",
  painAndMovement: "Pain & movement alert",
};

const THEME_KEYWORDS: Record<string, string[]> = {
  Sleep: ["sleep", "tired", "rest", "nap", "exhausted", "insomnia", "awake", "night"],
  Feeding: ["feed", "breastfeed", "bottle", "milk", "latch", "nursing", "formula"],
  Confidence: ["confident", "confidence", "proud", "capable", "strong", "achievement"],
  Support: ["support", "help", "partner", "family", "friend", "lonely", "alone"],
  Emotions: ["emotion", "cry", "happy", "sad", "anxious", "anxiety", "mood", "feeling", "overwhelm"],
  Body: ["body", "pain", "healing", "recovery", "weight", "scar", "bleeding", "exercise"],
  Baby: ["baby", "newborn", "infant", "child", "daughter", "son"],
  Bonding: ["bond", "bonding", "love", "connect", "attachment", "cuddle"],
};

function parseDateId(dateId: string): Date | null {
  const parts = dateId.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function formatDateId(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}

function formatDisplayDate(date: Date): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function getMoodLabel(score: number): "Low" | "Average" | "Good" | "Great" {
  if (score < 2) return "Low";
  if (score < 3) return "Average";
  if (score < 4) return "Good";
  return "Great";
}

function computeWeekNumber(startDate: Date, logDateId: string): number {
  const logDate = parseDateId(logDateId);
  if (!logDate) return 1;
  const diffMs = logDate.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return Math.min(6, Math.max(1, Math.floor(diffDays / 7) + 1));
}

function formatBirthType(answer: string | undefined): string {
  if (answer === "c_section") return "C-section";
  if (answer === "vaginal") return "Vaginal birth";
  if (answer === "assisted_vaginal") return "Assisted vaginal birth";
  return "Birth type unknown";
}

function computeAge(birthDate: Date): number {
  const now = new Date();
  let age = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function severityFromRank(rank: number): "none" | "low" | "moderate" | "high" {
  if (rank === 0) return "none";
  if (rank === 1) return "low";
  if (rank === 2) return "moderate";
  return "high";
}

interface DailyLog {
  dateId: string;
  mood?: { key?: string; score?: number };
  symptoms?: Record<string, {
    level?: string;
    flow?: string;
    colour?: string;
    clots?: string;
    colourRisk?: string;
    clotRisk?: string;
    overallRisk?: string;
  }>;
  symptomAlerts?: { hasAnyAlert?: boolean; alertSymptoms?: string[] };
  activities?: Record<string, { completed?: boolean }>;
}

export async function generateGPSummaryData(uid: string): Promise<GPSummaryData> {
  const [
    programmeSnap,
    deliverySnap,
    birthTypeSnap,
    logsSnap,
    journalSnap,
    userSnap,
    gpAppointmentSnap,
    midwifeSnap,
    ...weekSnaps
  ] = await Promise.all([
    getDoc(doc(db, `users/${uid}/recoveryProgrammes/physical_wellbeing_6w`)),
    getDoc(doc(db, `questionnaires/${uid}/responses/FTU.A.1`)),
    getDoc(doc(db, `questionnaires/${uid}/responses/FTU.A.2`)),
    getDocs(query(collection(db, `users/${uid}/recoveryDailyLogs`), orderBy("dateId"), limit(42))),
    getDocs(collection(db, `users/${uid}/journalEntries`)),
    getDoc(doc(db, `users/${uid}`)),
    getDoc(doc(db, `questionnaires/${uid}/responses/SYS.PROMPT.DATE.gp6w`)),
    getDoc(doc(db, `questionnaires/${uid}/responses/SYS.PROMPT.DATE.midwife`)),
    ...[1, 2, 3, 4, 5, 6].map((w) => getDoc(doc(db, `program/week_${w}`))),
    ...[1, 2, 3, 4, 5, 6].map((w) => getDoc(doc(db, `users/${uid}/recoveryWeeklyActivities/physical_wellbeing_6w_${w}`))),
  ]);

  const progWeekSnaps = weekSnaps.slice(0, 6);
  const actWeekSnaps = weekSnaps.slice(6, 12);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const programmeData = programmeSnap.data() as Record<string, any> | undefined;
  const startDateId = (programmeData?.startDateId as string) ?? "";
  const confirmedWeek = (programmeData?.confirmedWeek as number) ?? 0;

  const deliveryAnswer = deliverySnap.data()?.answer as string | undefined;
  const birthTypeAnswer = birthTypeSnap.data()?.answer as string | undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userData = userSnap.data() as Record<string, any> | undefined;

  const logs: DailyLog[] = logsSnap.docs.map((d) => ({
    dateId: d.id,
    ...d.data(),
  })) as DailyLog[];

  const journalEntries: { text?: string }[] = journalSnap.docs.map((d) => d.data() as { text?: string });

  // ─── 1. User Info ─────────────────────────────────────────────────────────

  const startDate = parseDateId(startDateId);
  const deliveryDate = deliveryAnswer ? parseDateId(deliveryAnswer) : null;
  let birthTypeText = formatBirthType(birthTypeAnswer);

  if (birthTypeText === "Birth type unknown") {
    const hasCSection = logs.some(
      (l) => l.symptoms?.cSectionWound?.level && l.symptoms.cSectionWound.level !== "none"
    );
    const hasPerineal = logs.some(
      (l) => l.symptoms?.perinealPain?.level && l.symptoms.perinealPain.level !== "none"
    );
    if (hasCSection) {
      birthTypeText = "C-section (inferred from tracked symptoms)";
    } else if (hasPerineal) {
      birthTypeText = "Vaginal birth (inferred from tracked symptoms)";
    }
  }

  let dateOfBirthText: string | null = null;
  const rawBirthDate = userData?.birthDate;
  if (rawBirthDate) {
    let birthDateObj: Date | null = null;
    if (rawBirthDate instanceof Timestamp) {
      birthDateObj = rawBirthDate.toDate();
    } else if (typeof rawBirthDate === "object" && "toDate" in rawBirthDate) {
      birthDateObj = rawBirthDate.toDate();
    } else if (typeof rawBirthDate === "string") {
      birthDateObj = parseDateId(rawBirthDate);
      if (!birthDateObj) {
        const isoDate = new Date(rawBirthDate);
        if (!isNaN(isoDate.getTime())) birthDateObj = isoDate;
      }
    }
    if (birthDateObj) {
      const age = computeAge(birthDateObj);
      dateOfBirthText = `${formatDisplayDate(birthDateObj)} (age ${age})`;
    }
  }

  const gpAppointmentAnswer = gpAppointmentSnap.data()?.answer as string | undefined;
  const midwifeAnswer = midwifeSnap.data()?.answer as string | undefined;
  const gpAppointmentDate = gpAppointmentAnswer ? parseDateId(gpAppointmentAnswer) : null;
  const midwifeDateObj = midwifeAnswer ? parseDateId(midwifeAnswer) : null;

  const userInfo: GPSummaryUserInfo = {
    fullName: (userData?.fullName as string) ?? "Name not provided",
    dateOfBirth: dateOfBirthText,
    deliveryDate: deliveryDate ? formatDisplayDate(deliveryDate) : "Unknown",
    birthType: birthTypeText,
    gpAppointmentDate: gpAppointmentDate ? formatDisplayDate(gpAppointmentDate) : null,
    midwifeDate: midwifeDateObj ? formatDisplayDate(midwifeDateObj) : null,
  };

  const timelineAnchor = deliveryDate ?? startDate;

  // ─── 2. Meta ──────────────────────────────────────────────────────────────

  const meta: GPSummaryMeta = {
    dateRange: timelineAnchor
      ? `${formatDisplayDate(timelineAnchor)} - ${formatDisplayDate(addDays(timelineAnchor, 41))}`
      : "Date range unavailable",
    completedWeek: confirmedWeek > 0
      ? `Week ${confirmedWeek} complete`
      : "Programme not started",
    generatedAt: new Date().toISOString(),
  };

  // ─── 3. Mood ─────────────────────────────────────────────────────────────

  const weekScores: Record<number, number[]> = {};
  for (const log of logs) {
    if (log.mood?.score != null && timelineAnchor) {
      const wk = computeWeekNumber(timelineAnchor, log.dateId);
      if (!weekScores[wk]) weekScores[wk] = [];
      weekScores[wk].push(log.mood.score);
    }
  }

  const mood: MoodWeek[] = [];
  for (let w = 1; w <= 6; w++) {
    const scores = weekScores[w];
    if (scores && scores.length > 0) {
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      const rounded = Math.round(avg * 10) / 10;
      mood.push({ week: `W${w}`, score: rounded, label: getMoodLabel(rounded) });
    }
  }

  const allScores = mood.map((m) => m.score);
  const moodAvgNum = allScores.length > 0
    ? allScores.reduce((a, b) => a + b, 0) / allScores.length
    : 0;
  const moodAvg = allScores.length > 0 ? moodAvgNum.toFixed(1) : "-";

  let moodTrend: GPSummarySnapshot["moodTrend"] = "insufficient";
  let moodTrendText = "Not enough mood data to determine a trend.";
  if (mood.length >= 2) {
    const firstHalf = mood.slice(0, Math.floor(mood.length / 2));
    const secondHalf = mood.slice(Math.floor(mood.length / 2));
    const avgFirst = firstHalf.reduce((a, b) => a + b.score, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((a, b) => a + b.score, 0) / secondHalf.length;
    const diff = avgSecond - avgFirst;
    if (diff > 0.3) {
      moodTrend = "improving";
      moodTrendText = "Your mood has steadily improved - a meaningful sign of positive recovery.";
    } else if (diff < -0.3) {
      moodTrend = "declining";
      moodTrendText = "Your mood shows a declining trend - this is worth discussing with your GP.";
    } else {
      moodTrend = "stable";
      moodTrendText = "Your mood has remained relatively stable throughout the programme.";
    }
  }

  // ─── 4. Symptoms ─────────────────────────────────────────────────────────

  const symptoms: SymptomSummary[] = [];
  const symptomKeys = Object.keys(SYMPTOM_LABELS);

  for (const key of symptomKeys) {
    const config = SYMPTOM_LABELS[key];
    const logsWithSymptom = logs.filter((l) => l.symptoms?.[key]?.level != null);
    if (logsWithSymptom.length < 2) continue;

    const firstLog = logsWithSymptom[0];
    const lastLog = logsWithSymptom[logsWithSymptom.length - 1];
    const firstLevel = firstLog.symptoms![key].level!;
    const lastLevel = lastLog.symptoms![key].level!;

    const firstRank = SEVERITY_RANK[firstLevel] ?? 0;
    const lastRank = SEVERITY_RANK[lastLevel] ?? 0;

    let percent = 0;
    let status: SymptomSummary["status"] = "stable";

    if (firstRank > 0) {
      percent = Math.round(((firstRank - lastRank) / firstRank) * 100);
    }

    if (lastRank === 0 && firstRank > 0) {
      status = "resolved";
      percent = 100;
    } else if (lastRank < firstRank) {
      status = "improving";
    } else if (lastRank > firstRank) {
      status = "worsening";
      percent = 0;
    }

    const firstWeek = timelineAnchor ? computeWeekNumber(timelineAnchor, firstLog.dateId) : 1;
    const lastWeek = timelineAnchor ? computeWeekNumber(timelineAnchor, lastLog.dateId) : 6;

    symptoms.push({
      name: config.label,
      percent: Math.max(0, percent),
      from: `${config.levels[firstLevel] ?? firstLevel} (Wk ${firstWeek})`,
      to: `${config.levels[lastLevel] ?? lastLevel} (Wk ${lastWeek})`,
      status,
      severity: severityFromRank(lastRank),
    });
  }

  // ─── 5. Bleeding Log ────────────────────────────────────────────────────

  const bleedingLog: BleedingDay[] = [];
  for (let day = 1; day <= 42; day++) {
    const week = Math.ceil(day / 7);
    if (!timelineAnchor) {
      bleedingLog.push({ day, week, flow: "none", colour: null, clots: "none" });
      continue;
    }
    const dateId = formatDateId(addDays(timelineAnchor, day - 1));
    const log = logs.find((l) => l.dateId === dateId);
    const bleeding = log?.symptoms?.vaginalBleeding;

    if (bleeding) {
      bleedingLog.push({
        day,
        week,
        flow: (bleeding.flow as FlowLevel) ?? "none",
        colour: (bleeding.colour as BloodColour) ?? null,
        clots: (bleeding.clots as ClotSize) ?? "none",
      });
    } else {
      bleedingLog.push({ day, week, flow: "none", colour: null, clots: "none" });
    }
  }

  // ─── 6. Red Flags ───────────────────────────────────────────────────────

  const redFlags: RedFlag[] = [];
  for (const log of logs) {
    if (log.symptomAlerts?.hasAnyAlert) {
      const week = timelineAnchor ? computeWeekNumber(timelineAnchor, log.dateId) : 1;
      const alertSymptoms = log.symptomAlerts.alertSymptoms ?? [];
      const labels = alertSymptoms
        .map((s: string) => ALERT_LABELS[s] ?? `${s} alert`)
        .join(", ");
      redFlags.push({
        week: `Week ${week}`,
        label: labels || "Symptom alert triggered",
      });
    }
  }

  const seenFlags = new Set<string>();
  const uniqueRedFlags = redFlags.filter((f) => {
    const key = `${f.week}:${f.label}`;
    if (seenFlags.has(key)) return false;
    seenFlags.add(key);
    return true;
  });

  // ─── 7. Weekly Activities ───────────────────────────────────────────────

  const weeklyActivities: WeekActivities[] = [];
  for (let w = 0; w < 6; w++) {
    const weekNum = w + 1;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const progData = progWeekSnaps[w].data() as Record<string, any> | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const actData = actWeekSnaps[w].data() as Record<string, any> | undefined;

    const activityDefs = (progData?.activities as Array<{ id: string; title: string }>) ?? [];
    const completionMap = (actData?.activities as Record<string, boolean>) ?? {};

    const activities: WeekActivity[] = activityDefs.map((def) => ({
      id: def.id,
      title: def.title,
      completed: completionMap[def.id] === true,
    }));

    weeklyActivities.push({
      week: weekNum,
      activities,
      completedCount: activities.filter((a) => a.completed).length,
      totalCount: activities.length,
    });
  }

  // ─── 8. Journal Themes ──────────────────────────────────────────────────

  const themeCounts: Record<string, number> = {};
  const meaningfulEntries = journalEntries.filter((e) => {
    const text = (e.text ?? "").trim();
    if (text.length < 10) return false;
    const words = new Set(text.toLowerCase().split(/\s+/));
    return words.size > 1;
  });
  const allText = meaningfulEntries.map((e) => (e.text ?? "").toLowerCase()).join(" ");

  for (const [theme, keywords] of Object.entries(THEME_KEYWORDS)) {
    let count = 0;
    for (const kw of keywords) {
      const regex = new RegExp(`\\b${kw}\\b`, "gi");
      const matches = allText.match(regex);
      count += matches?.length ?? 0;
    }
    if (count > 0) {
      themeCounts[theme] = count;
    }
  }

  const journalThemes = Object.entries(themeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([theme]) => theme);

  if (journalThemes.length === 0 && journalEntries.length > 0) {
    journalThemes.push("General");
  }

  // ─── 9. Snapshot ─────────────────────────────────────────────────────────

  const totalActivitiesCompleted = weeklyActivities.reduce((sum, w) => sum + w.completedCount, 0);
  const totalActivitiesAvailable = weeklyActivities.reduce((sum, w) => sum + w.totalCount, 0);

  let bleedingStoppedDay: number | null = null;
  const lastBleedingDay = [...bleedingLog].reverse().find((d) => d.flow !== "none");
  if (lastBleedingDay) {
    bleedingStoppedDay = lastBleedingDay.day;
  }

  const snapshot: GPSummarySnapshot = {
    completedWeek: confirmedWeek,
    totalActivitiesCompleted,
    totalActivitiesAvailable,
    moodAverage: allScores.length > 0 ? Math.round(moodAvgNum * 10) / 10 : 0,
    moodTrend,
    symptomsImproving: symptoms.filter((s) => s.status === "improving").length,
    symptomsWorsening: symptoms.filter((s) => s.status === "worsening").length,
    symptomsResolved: symptoms.filter((s) => s.status === "resolved").length,
    redFlagCount: uniqueRedFlags.length,
    bleedingStoppedDay,
    journalEntryCount: journalEntries.length,
  };

  return {
    userInfo,
    snapshot,
    meta,
    mood,
    moodAverage: moodAvg,
    moodTrendText,
    symptoms,
    redFlags: uniqueRedFlags,
    bleedingLog,
    weeklyActivities,
    journalThemes,
  };
}
